/* eslint-disable @angular-eslint/prefer-inject */
import { ProjectLineItemDataService } from '../infrastructure/project-line-items/project-line-item.data.service';
import {
    ProjectLineItemCreate,
    ProjectLineItemDto,
    ProjectLineItemRead,
    ProjectLineItemSearchParams,
} from '../entities/project-line-item.dto';
import { ProjectLineItemStore } from '../infrastructure/project-line-items/project-line-item.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { ProjectLineItemMapper } from '../infrastructure/project-line-items/project-line-item.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import { PFWmsEntities, SafeAny } from '@pf/shared-common';
import {
    BehaviorSubject,
    debounceTime,
    filter,
    of,
    switchMap,
    tap,
} from 'rxjs';
import { untilDestroyed } from '@ngneat/until-destroy';
import { ProjectDataService } from '../infrastructure/projects/project.data.service';
import { WarehouseFacade } from './warehouse.facade';
import { ResourceAuth } from '@control-tower/platform-wms';

@Injectable()
export class ProjectLineItemFacade extends AbstractManageEntityFacade<
    ProjectLineItemDto,
    ProjectLineItemRead,
    ProjectLineItemCreate,
    ProjectLineItemSearchParams
> {
    nameField: keyof ProjectLineItemDto = 'lineNumber';
    private readonly projectDataService = inject(ProjectDataService);
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly projectsToRefresh = new BehaviorSubject<string[]>([]);

    constructor(
        dataService: ProjectLineItemDataService,
        store: ProjectLineItemStore,
        mapper: ProjectLineItemMapper
    ) {
        super(dataService, null, store, mapper);
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    override applyResourceAuth(body: ProjectLineItemCreate): void {
        body.resourceAuth = { allAuthorized: true } as ResourceAuth;
    }

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof ProjectLineItemDto, string>> {
        return { lineNumber: searchText };
    }

    refreshProjectsOnLineItemUpdates() {
        this.listenForRealTimeEvents(PFWmsEntities.ProjectLineItem, {
            processEventsIfNotInStore: true,
            customEntityMapping: (entity: ProjectLineItemRead) => {
                if (
                    entity.project?.id &&
                    !this.projectsToRefresh.value.includes(entity.project.id)
                ) {
                    this.projectsToRefresh.next([
                        ...this.projectsToRefresh.value,
                        entity.project.id,
                    ]);
                }
                return of(entity);
            },
        });

        this.projectsToRefresh
            .pipe(
                untilDestroyed(this),
                debounceTime(1000),
                filter(ids => ids.length > 0),
                switchMap(projectIds =>
                    this.projectDataService.getMany$(projectIds, true)
                ),
                tap(() => this.projectsToRefresh.next([]))
            )
            .subscribe();
    }
}
